import { create } from 'zustand';
import type { Cluster } from '../lib/types';
import { listClusters } from '../lib/api';

const STORAGE_KEY = 'fm.activeCluster';

type ActiveSlug = string | 'all';

interface ClusterState {
  clusters: Cluster[];
  activeSlug: ActiveSlug;
  stackFilter: string;
  loaded: boolean;
  setClusters: (list: Cluster[]) => void;
  setActive: (slug: ActiveSlug) => void;
  setStackFilter: (stack: string) => void;
  fetchClusters: () => Promise<void>;
}

const FLOW_PARAM = 'flow';

function readFlowParam(): string {
  if (typeof location === 'undefined') return '';
  return new URLSearchParams(location.search).get(FLOW_PARAM) ?? '';
}

function writeSession(slug: ActiveSlug): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, slug);
  } catch {
    /* ignore quota / unavailable storage */
  }
}

// O flow ativo é por aba (#32379): sessionStorage em vez de localStorage, que
// era compartilhado e fazia o F5 numa aba puxar o flow escolhido noutra.
// ?flow=<slug> na URL vence o que a aba tinha, para link favoritado ou
// compartilhado abrir no cluster certo. A chave antiga do localStorage é
// consumida uma vez e apagada, para ninguém cair em Todos após o deploy.
export function readInitialActive(): ActiveSlug {
  const fromUrl = readFlowParam();
  if (fromUrl.length > 0) {
    writeSession(fromUrl);
    return fromUrl;
  }
  if (typeof sessionStorage === 'undefined') return 'all';
  try {
    const own = sessionStorage.getItem(STORAGE_KEY);
    if (own && own.length > 0) return own;
    const legacy = localStorage.getItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY);
    if (legacy && legacy.length > 0) {
      writeSession(legacy);
      return legacy;
    }
    return 'all';
  } catch {
    return 'all';
  }
}

export const useClusterStore = create<ClusterState>(set => ({
  clusters: [],
  activeSlug: readInitialActive(),
  stackFilter: '',
  loaded: false,
  setClusters: list => set({ clusters: list, loaded: true }),
  setActive: slug => set({ activeSlug: slug, stackFilter: '' }),
  setStackFilter: stack => set({ stackFilter: stack }),
  fetchClusters: async () => {
    try {
      const list = await listClusters();
      set({ clusters: list, loaded: true });
    } catch {
      set({ clusters: [], loaded: true });
    }
  },
}));

useClusterStore.subscribe(state => writeSession(state.activeSlug));

export const clusterStore = useClusterStore;
